import { LatLngExpression } from 'leaflet'
import { memo, useEffect, useState } from 'react'
import { Polyline, Tooltip, useMap, useMapEvent } from 'react-leaflet'
import { getPositionName } from 'src/utils/function'
import { IPosition } from '../../../../../generated-types'
import {
  deviationPositions,
  grayedOpacity,
  mapOptions,
  minZoom,
  opacity,
  totalDeviationMultiplier,
} from '../constraints'
import { useMooringsCtx } from 'src/hooks/context-hooks'
import { useShowHiddenPositions } from '../ShowHiddenPositionsContext'
import {
  destVincenty,
  getBearing,
  getConnectedPositionCoordinates,
  getDistance,
  getLineHitAreaWeight,
  midpoint,
} from '../utils'

interface IBridle {
  position: IPosition
  navigateToPosition: (positionId: number) => void
  showCriticalPositions?: boolean
}

export const Bridle: React.FC<IBridle> = memo(({ position, navigateToPosition, showCriticalPositions = false }) => {
  const showHiddenPositions = useShowHiddenPositions()
  const generateBridles = true

  const map = useMap()
  const [zoomLevel, setZoomLevel] = useState(Math.round(map.getZoom()))

  useEffect(() => {
    setZoomLevel(Math.floor(map.getZoom()))
  }, [map.getZoom()])

  const handleZoomEnd = event => {
    const newZoomLevel = event.target.getZoom()
    setZoomLevel(Math.round(newZoomLevel))
  }
  useMapEvent('zoomend', handleZoomEnd)
  const { isCritical } = useMooringsCtx()
  const shouldHighlightCritical = !!(isCritical || showCriticalPositions)

  const corner1 = getConnectedPositionCoordinates(position.connectedPositions[0])
  const corner2 = getConnectedPositionCoordinates(position.connectedPositions[1])

  // Hidden positions are drawn greyed out when "show hidden" is on, so they can be found.
  if (position.isHidden && !showHiddenPositions) return <></>
  if (!generateBridles || !corner1 || !corner2) return <></>
  if (position.isHidden) position = { ...position, isTransparent: true }

  const content = mapOptions.lang.bridle + ' ' + getPositionName(position)

  const bearing = getBearing(corner1.latitude, corner1.longitude, corner2.latitude, corner2.longitude)

  const distance = getDistance(corner1.latitude, corner1.longitude, corner2.latitude, corner2.longitude)

  const destPos1 = destVincenty(corner1.latitude, corner1.longitude, bearing - 18, (distance / 100) * 50)

  const destPos2 = destVincenty(corner1.latitude, corner1.longitude, bearing, (distance / 100) * 50)

  const destPos3 = destVincenty(corner1.latitude, corner1.longitude, bearing + 18, (distance / 100) * 50)

  const origin: LatLngExpression = [position.connectedPositions[0].latitude, position.connectedPositions[0].longitude]

  let lines: LatLngExpression[][]
  if (position.bridleFeets === 1) {
    lines = [[origin, [+destPos2[0], +destPos2[1]]]]
  } else if (position.bridleFeets === 2) {
    lines = [
      [origin, [+destPos1[0], +destPos1[1]]],
      [origin, [+destPos3[0], +destPos3[1]]],
    ]
  } else {
    lines = [
      [origin, [+destPos1[0], +destPos1[1]]],
      [origin, [+destPos2[0], +destPos2[1]]],
      [origin, [+destPos3[0], +destPos3[1]]],
    ]
  }

  const pathOptions = {
    simplepoly: 2,
    fillOpacity: 0,
    opacity: position.isTransparent ? grayedOpacity : opacity,
    weight: (zoomLevel <= minZoom ? 1 : zoomLevel - minZoom) + (position.isCritical && shouldHighlightCritical ? 2 : 0),
    color:
      position.deviationsCount > 0
        ? opacity !== 1.0
          ? mapOptions.bridleColor
          : mapOptions.deviationColor
        : mapOptions.bridleColor,
    pane: position.isWorkAssignmentPosition ? 'bridleUpdated' : 'bridle',
  }

  if (position.deviationsCount != 0) {
    const mid = midpoint(corner1.latitude, corner1.longitude, +destPos2[0], +destPos2[1], 0.5)
    deviationPositions.push([mid[0], mid[1], totalDeviationMultiplier * position.deviationsCount])
  }

  return (
    <>
      {lines.map(line => (
        <>
          <Polyline
            key={position.id}
            positions={line}
            pathOptions={pathOptions}
            pane={pathOptions.pane}
            interactive={false}
          />
          <Polyline
            positions={line}
            pathOptions={{
              ...pathOptions,
              weight: getLineHitAreaWeight(pathOptions.weight),
              opacity: 0,
              pane: 'bridleHitArea',
            }}
            pane="bridleHitArea"
            eventHandlers={{
              click: () => {
                navigateToPosition(position.id)
              },
            }}
          >
            <Tooltip sticky>
              <strong>{content}</strong>
            </Tooltip>
          </Polyline>
        </>
      ))}
    </>
  )
})
